import type { Question } from '@/types/theme.types';
import type { SessionTally } from './types';

type Ordered = { id: string; order?: unknown };

/** Questions in the order the sector defines them; ties keep their original order. */
export function orderQuestions<T extends Ordered>(questions: readonly T[]): T[] {
  return questions
    .map((question, index) => ({ question, index, order: Number(question.order ?? 0) || 0 }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ question }) => question);
}

/**
 * Next question still to handle after `from`: forward first, then from the
 * start, skipping what was submitted or skipped. -1 when nothing is left.
 */
export function nextPendingIndex(
  questions: readonly Ordered[],
  from: number,
  done: ReadonlySet<string>,
): number {
  for (let index = from + 1; index < questions.length; index += 1) {
    if (!done.has(questions[index].id)) return index;
  }
  for (let index = 0; index <= Math.min(from, questions.length - 1); index += 1) {
    if (!done.has(questions[index].id)) return index;
  }
  return -1;
}

export function firstPendingIndex(questions: readonly Ordered[], done: ReadonlySet<string>): number {
  return nextPendingIndex(questions, -1, done);
}

export function tallySession(
  questions: readonly Ordered[],
  submitted: ReadonlySet<string>,
  skipped: ReadonlySet<string>,
): SessionTally {
  const total = questions.length;
  let submittedCount = 0;
  let skippedCount = 0;
  for (const question of questions) {
    if (submitted.has(question.id)) submittedCount += 1;
    else if (skipped.has(question.id)) skippedCount += 1;
  }
  const remaining = Math.max(0, total - submittedCount - skippedCount);
  const percent = total > 0 ? Math.round((submittedCount / total) * 100) : 0;
  return { total, submitted: submittedCount, skipped: skippedCount, remaining, percent };
}

/** A past initial-data contribution, as GET /themes/contributions/:sectorId returns it. */
export type PastContribution = {
  questionId?: string | null;
  phraseId?: string | null;
  language?: string | null;
  responseAudioLocalUrl?: string | null;
  responseAudioUrl?: string | null;
};

// Written answers are stored with this placeholder instead of an audio URL.
const TEXT_ANSWER_URL_PREFIX = 'initial-text://';

/**
 * Questions already answered out loud in `language`. The backend stores the
 * question as `questionId`; `phraseId` is kept for older records.
 */
export function answeredAudioQuestionIds(
  contributions: readonly PastContribution[],
  language: string,
): Set<string> {
  const ids = new Set<string>();
  for (const contribution of contributions) {
    if (contribution.language !== language) continue;
    const audioUrl = contribution.responseAudioLocalUrl ?? contribution.responseAudioUrl ?? '';
    if (audioUrl.startsWith(TEXT_ANSWER_URL_PREFIX)) continue;
    const id = contribution.questionId ?? contribution.phraseId;
    if (id) ids.add(String(id));
  }
  return ids;
}

/** About 40 s per spoken answer and 60 s per written one. */
export function estimateMinutes(questionCount: number, mode: 'audio' | 'text'): number {
  if (questionCount <= 0) return 0;
  const secondsPerQuestion = mode === 'audio' ? 40 : 60;
  return Math.max(1, Math.round((questionCount * secondsPerQuestion) / 60));
}

/** Marker window around the current question, so long sectors stay readable. */
export function markerWindow(total: number, current: number, size = 10): number[] {
  if (total <= 0) return [];
  const width = Math.min(size, total);
  const start = Math.min(Math.max(0, current - Math.floor(width / 2)), total - width);
  return Array.from({ length: width }, (_, offset) => start + offset);
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Local wording of a question, falling back to French. */
export function questionLocalText(question: Pick<Question, 'textLocal' | 'textFr'>): string {
  return (question.textLocal || question.textFr || '').trim();
}
